import {FilterOption} from "../types/filterTypes";
import {ContentSpecificParams} from "../types/contentTypes";
import {HIGHLIGHT_OPTION_SHEMA} from "../types/keyMomentTypes";
import {formatSafeUTCDate} from "@/utils/utils";

export enum TARGET_APP_BUILD_VALUE{
    BACKUP= "backup",
    STANDARD = "standard",
}

export const TARGET_APP_BUILD = (import.meta as any).env.VITE_TARGET_BUILD as TARGET_APP_BUILD_VALUE

export const BACKEND_URL = TARGET_APP_BUILD === TARGET_APP_BUILD_VALUE.BACKUP ?
    `https://${(import.meta as any).env.VITE_BACKEND_HOSTNAME}`
    : `${window.location.protocol}//${(import.meta as any).env.VITE_BACKEND_HOSTNAME}${(import.meta as any).env.VITE_BACKEND_PORT ? ':' + (import.meta as any).env.VITE_BACKEND_PORT : '' }/api`;

export enum SUPPORTED_SPORTS {
    FOOTBALL = "FOOTBALL",
    TENNIS = "TENNIS",
    PADEL = "PADEL",
    VOLLEY = "VOLLEY",
    BASKETBALL = "BASKETBALL"
}

export const CURRENT_SPORT: SUPPORTED_SPORTS = (import.meta as any).env.VITE_APP_SPORT.toUpperCase()

export const SUPPORTED_CONTENTS = {
    [SUPPORTED_SPORTS.FOOTBALL]: {
        MATCH_HIGHLIGHT: "MATCH_HIGHLIGHT",
        GOAL_PARADE: "GOAL_PARADE",
        CLIP: "CLIP",
    },
    [SUPPORTED_SPORTS.BASKETBALL]: {
        MATCH_HIGHLIGHT: "MATCH_HIGHLIGHT",
        INTERVIEW: "INTERVIEW",
        CLIP: "CLIP",
    },
    [SUPPORTED_SPORTS.TENNIS]: {
        MATCH_HIGHLIGHT: "MATCH_HIGHLIGHT",
        INTERVIEW: "INTERVIEW",
        CLIP: "CLIP",
    },
    [SUPPORTED_SPORTS.PADEL]: {
        MATCH_HIGHLIGHT: "MATCH_HIGHLIGHT",
        CLIP: "CLIP",
    },
     [SUPPORTED_SPORTS.VOLLEY]: {
        MATCH_HIGHLIGHT: "MATCH_HIGHLIGHT",
        CLIP: "CLIP",
    },
} as const;

type SupportedContentsConfig = typeof SUPPORTED_CONTENTS;

type DefaultClipNamesConfig = {
    [Sport in keyof SupportedContentsConfig]: {
        [Content in keyof SupportedContentsConfig[Sport]]: (dynamicText: string, other: string, other2: string) => string;
    }
};

export const DEFAULT_CLIP_NAMES: DefaultClipNamesConfig = {
    [SUPPORTED_SPORTS.FOOTBALL]: {
        MATCH_HIGHLIGHT: (homeTeam : string, awayTeam : string, date : string) => homeTeam && awayTeam && date ? `HL ${homeTeam} - ${awayTeam} / ${formatSafeUTCDate(date, false, false)}` : `Highlight of ${homeTeam} - ${awayTeam}`,
        GOAL_PARADE: (matchDay: string, competition?: string) => `Goal Parade of ${competition}, Match day: ${matchDay}`,
        CLIP: (playerName?: string) => playerName ? `${playerName}'s action (Soccer)` : "New Soccer Clip",
    },
    [SUPPORTED_SPORTS.TENNIS]: {
        MATCH_HIGHLIGHT: (matchName: string) => matchName ? `Highlights Match - ${matchName}` : "Highlights Match",
        INTERVIEW: (player: string) => player ? `Postgame interview: ${player}` : "On-Field Interview",
        CLIP: (shotType: string) => shotType ? `New Tennis Rally: (${shotType})` : "New Tennis Rally",
    },
    [SUPPORTED_SPORTS.PADEL]: {
        MATCH_HIGHLIGHT: (tournament: string) => tournament ? `Highlights Set ${tournament}` : "Highlights Set",
        CLIP: (players: string) => players ? `New Padel Point for ${players}` : "New Padel Point",
    },
    [SUPPORTED_SPORTS.VOLLEY]: {
        MATCH_HIGHLIGHT: (tournament: string) => tournament ? `Highlights Set ${tournament}` : "Highlights Set",
        CLIP: (players: string) => players ? `New Volley Point for ${players}` : "New Volley Point",
    },
    [SUPPORTED_SPORTS.BASKETBALL]: {
        MATCH_HIGHLIGHT: (tournament: string) => tournament ? `Highlights Set ${tournament}` : "Highlights Set",
        INTERVIEW: (player: string) => player ? `Postgame interview: ${player}` : "On-Field Interview",
        CLIP: (players: string) => players ? `New BasketBall Point for ${players}` : "New BasketBall Point",
    },
};

export const DEFAULT_SORT_FIELD: Record<SUPPORTED_SPORTS, {platform:FilterOption | undefined, local:FilterOption | undefined}> = {
    [SUPPORTED_SPORTS.FOOTBALL]: {
        local: {
            id: 'play_time',
            value: 'Game Time'
        },
        platform: {
            id: 'global_seconds',
            value: 'Gametime'
        },
    },
    [SUPPORTED_SPORTS.VOLLEY]: {
        local: {
            id: 'global_seconds',
            value: 'Game Time'
        },
        platform: {
            id: 'global_seconds',
            value: 'Gametime'
        },
    },
    [SUPPORTED_SPORTS.TENNIS]: {
        platform: undefined,
        local: undefined
    },
    [SUPPORTED_SPORTS.PADEL]: {
        platform: undefined,
        local: undefined
    },
    [SUPPORTED_SPORTS.BASKETBALL]: {
        platform: undefined,
        local: undefined
    }
}

export const KEY_MOMENTS_SORTABLE_FIELDS: Record<SUPPORTED_SPORTS, {highlight:FilterOption[], local:FilterOption[], platform: FilterOption[]}> = {
    [SUPPORTED_SPORTS.FOOTBALL]: {
        highlight:[
            {
                id: 'play_time',
                value: 'Game Time'
            }],
        local: [
            {
                id: 'created_at',
                value: 'Created At'
            },
            {
                id: 'updated_at',
                value: 'Updated At'
            },
            {
                id: 'play_time',
                value: 'Game Time'
            },
            {
                id: 'title',
                value: 'Type'
            },
            {
                id: 'event_time',
                value: 'Event'
            }
        ],
        platform: [
            {
                id: 'created_at',
                value: 'Created At'
            },
            {
                id: 'updated_at',
                value: 'Updated At'
            },
            {
                id: 'global_seconds',
                value: 'Game Time'
            },
            {
                id: 'type',
                value: 'Type'
            }
        ]
    },
    [SUPPORTED_SPORTS.TENNIS]: {
        highlight:[
            {
                id: 'play_time',
                value: 'Game Time'
            }],
        local: [
            {
                id: 'created_at',
                value: 'Created At'
            },
            {
                id: 'updated_at',
                value: 'Updated At'
            },
            {
                id: 'play_time',
                value: 'Game Time'
            },
            {
                id: 'title',
                value: 'Type'
            }
        ],
        platform: [
            {
                id: 'created_at',
                value: 'Created At'
            },
            {
                id: 'updated_at',
                value: 'Updated At'
            },
            {
                id: 'global_seconds',
                value: 'Gametime'
            },
            {
                id: 'type',
                value: 'Type'
            }
        ]
    },
    [SUPPORTED_SPORTS.BASKETBALL]: {
        highlight:[
            {
                id: 'play_time',
                value: 'Game Time'
            }],
        local: [
            {
                id: 'created_at',
                value: 'Created At'
            },
            {
                id: 'updated_at',
                value: 'Updated At'
            },
            {
                id: 'play_time',
                value: 'Game Time'
            },
            {
                id: 'title',
                value: 'Type'
            }
        ],
        platform: [
            {
                id: 'created_at',
                value: 'Created At'
            },
            {
                id: 'updated_at',
                value: 'Updated At'
            },
            {
                id: 'global_seconds',
                value: 'Gametime'
            },
            {
                id: 'type',
                value: 'Type'
            }
        ]
    },
    [SUPPORTED_SPORTS.PADEL]: {
        highlight:[
            {
                id: 'play_time',
                value: 'Game Time'
            }],
        local: [
            {
                id: 'created_at',
                value: 'Created At'
            },
            {
                id: 'updated_at',
                value: 'Updated At'
            },
            {
                id: 'play_time',
                value: 'Game Time'
            },
            {
                id: 'title',
                value: 'Type'
            }
        ],
        platform: [
            {
                id: 'created_at',
                value: 'Created At'
            },
            {
                id: 'updated_at',
                value: 'Updated At'
            },
            {
                id: 'global_seconds',
                value: 'Gametime'
            },
            {
                id: 'type',
                value: 'Type'
            }
        ]
    },
    [SUPPORTED_SPORTS.VOLLEY]: {
        highlight:[
            {
                id: 'global_seconds',
                value: 'Stream Time'
            }],
        local: [
            {
                id: 'created_at',
                value: 'Created At'
            },
            {
                id: 'updated_at',
                value: 'Updated At'
            },
            {
                id: 'global_seconds',
                value: 'Stream Time'
            },
            {
                id: 'title',
                value: 'Type'
            }
        ],
        platform: [
            {
                id: 'created_at',
                value: 'Created At'
            },
            {
                id: 'updated_at',
                value: 'Updated At'
            },
            {
                id: 'global_seconds',
                value: 'Stream Time'
            },
            {
                id: 'type',
                value: 'Type'
            }
        ]
    },
};

export enum MAIN_CONTENT_TYPES {
    KEY_MOMENTS = 'keymoments',
    CLIP = 'clip'
}

export type SportContentMap = typeof SUPPORTED_CONTENTS;

export type SportContent<S extends SUPPORTED_SPORTS> = keyof SportContentMap[S];

export const MATCH_HIGHLIGHT_OPTION = [
    {
        name: "START",
        options: {}
    },
    {
        name: "GOAL",
        options: {
            schema:  HIGHLIGHT_OPTION_SHEMA.CHECKBOX,
            values : [{
                label: "Replay",
                name: "REPLAY",
                value: false,
            },
            {
                label: "VAR",
                name: "VAR",
                value: true,
            }
            ]
        }
    },
    {
        name: "SAVE",
        options: {}
    },
    {
        name: "MISS",
        options: {}
    },
    {
        name: "CARD",
        options: {
            schema: HIGHLIGHT_OPTION_SHEMA.RADIO_BUTTON,
            values : [{
                label: "Red Only",
                name: "RED_ONLY",
                value: false,
            },
            {
                label: "All",
                name: "ALL",
                value: true,
            }]
        }
    },
    {
        name: "END",
        options: {}
    }
];

export const FOOTBALL_MATCH_HIGHLIGHT_OPTION = [
    {
        name: "START",
        options: {}
    },
    {
        name: "GOAL",
        options: {
            schema:  HIGHLIGHT_OPTION_SHEMA.CHECKBOX,
            values : [{
                label: "Replay",
                name: "REPLAY",
                value: false,
            },
            {
                label: "VAR",
                name: "VAR",
                value: true,
            }
            ]
        }
    },
    {
        name: "SAVE",
        options: {}
    },
    {
        name: "MISS",
        options: {}
    },
    {
        name: "CARD",
        options: {
            schema: HIGHLIGHT_OPTION_SHEMA.RADIO_BUTTON,
            values : [{
                label: "Red Only",
                name: "RED_ONLY",
                value: false,
            },
            {
                label: "All",
                name: "ALL",
                value: true,
            }]
        }
    },
    {
        name: "END",
        options: {}
    }
];


export const VOLLEY_MATCH_HIGHLIGHT_OPTION = [
    {
        name: "RALLY_POINT",
        options: {
            schema:  HIGHLIGHT_OPTION_SHEMA.CHECKBOX,
            values : [{
                label: "Replay",
                name: "REPLAY",
                value: false,
            }]
        }
    },
    {
        name: "SET_POINT",
        options: {
            schema:  HIGHLIGHT_OPTION_SHEMA.CHECKBOX,
            values : [{
                label: "Replay",
                name: "REPLAY",
                value: false,
            }]
        }
    },
    {
        name: "MATCH_POINT",
        options: {
            schema:  HIGHLIGHT_OPTION_SHEMA.CHECKBOX,
            values : [{
                label: "Replay",
                name: "REPLAY",
                value: false,
            }]
        }
    },
];

export const whitelistedPlaceholders = ['home_team', 'away_team', 'event_name', 'competition_name', 'key_moment_type', 'player']

export const GOAL_PARADE_OPTION = [
    {
        name: "GOAL",
        options: {
            schema:  HIGHLIGHT_OPTION_SHEMA.CHECKBOX,
            values : [{
                label: "Replay",
                name: "REPLAY",
                value: false,
            },
            {
                label: "VAR",
                name: "VAR",
                value: true,
            }
            ]
        }
    }
];

export const defaultContentSpecificParams: ContentSpecificParams = {
    [SUPPORTED_SPORTS.FOOTBALL]: {
        [SUPPORTED_CONTENTS.FOOTBALL.MATCH_HIGHLIGHT]: {
            minDuration: 240,
            maxDuration: 300,
            match: null,
            competition: null,
            keyMomentSettings: FOOTBALL_MATCH_HIGHLIGHT_OPTION.map((hlOption) => ({selected: false, ...hlOption}))
        },
        [SUPPORTED_CONTENTS.FOOTBALL.GOAL_PARADE]: {
            minDuration: 240,
            maxDuration: 300,
            matchday: '',
            competition: null,
            keyMomentSettings: GOAL_PARADE_OPTION.map((hlOption) => ({selected: hlOption.name === 'GOAL', ...hlOption}))
        }
    },
    [SUPPORTED_SPORTS.VOLLEY]: {
        [SUPPORTED_CONTENTS.VOLLEY.MATCH_HIGHLIGHT]: {
            minDuration: 240,
            maxDuration: 300,
            match: null,
            competition: null,
            keyMomentSettings: VOLLEY_MATCH_HIGHLIGHT_OPTION.map((hlOption) => ({selected: false, ...hlOption}))
        },
    },
    [SUPPORTED_SPORTS.TENNIS]: {
        [SUPPORTED_CONTENTS.TENNIS.MATCH_HIGHLIGHT]: {
            minDuration: 240,
            maxDuration: 300,
            match: null,
            competition: null,
            keyMomentSettings: MATCH_HIGHLIGHT_OPTION.map((hlOption) => ({selected: false, ...hlOption}))
        },
    },
    [SUPPORTED_SPORTS.PADEL]: {
        [SUPPORTED_CONTENTS.PADEL.MATCH_HIGHLIGHT]: {
            minDuration: 240,
            maxDuration: 300,
            match: null,
            competition: null,
            keyMomentSettings: MATCH_HIGHLIGHT_OPTION.map((hlOption) => ({selected: false, ...hlOption}))
        },
    },
    [SUPPORTED_SPORTS.BASKETBALL]: {
        [SUPPORTED_CONTENTS.BASKETBALL.MATCH_HIGHLIGHT]: {
            minDuration: 240,
            maxDuration: 300,
            match: null,
            competition: null,
            keyMomentSettings: MATCH_HIGHLIGHT_OPTION.map((hlOption) => ({selected: false, ...hlOption}))
        },
    },
}


export const CONTENT_AGGREGATION_PAGE_NAME = 'contentAggregation';
export const EDITOR_PAGE_NAME = 'editor';
export const KEY_MOMENT_PAGE_NAME = 'keymoments';
export const CLIP_PAGE_NAME = 'clips';
export const LIBRARY_PAGE_NAME = 'library';
export const AUTOMATION_FORGE = 'automation_forge';
export const PLUGIN_PAGE_NAME = 'plugin';
export const BACKUP_PAGE_NAME = 'backup';

export const EVENTS_SECTION_BY_SPORT = {
    [SUPPORTED_SPORTS.FOOTBALL]: { path: 'matches', label: 'Calendar' },
    [SUPPORTED_SPORTS.BASKETBALL]: { path: 'events', label: 'Events' },
    [SUPPORTED_SPORTS.TENNIS]: { path: 'events', label: 'Events' },
    [SUPPORTED_SPORTS.PADEL]: { path: 'events', label: 'Events' },
    [SUPPORTED_SPORTS.VOLLEY]: { path: 'events', label: 'Events' },
} as const;

export const EVENTS_PAGE_NAME = EVENTS_SECTION_BY_SPORT[CURRENT_SPORT].path;
export const EVENTS_PAGE_TITLE = EVENTS_SECTION_BY_SPORT[CURRENT_SPORT].label;

export const EDITORIAL_ASSET_MOCK = [
    {
        url: 'http://localhost:8080/opener.webm',
        mimeType: 'video/webm',
        duration: 5,
        startTime: 0,
    },
    {
        url: 'https://d10aasai5cq85w.cloudfront.net/Events/8555ad0d-258a-4ec4-a143-ef589fa1bb8f/ee18c792-e0a6-4500-a9ce-01ebe5b362af/Stream/stream.m3u8?Policy=eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly9kMTBhYXNhaTVjcTg1dy5jbG91ZGZyb250Lm5ldC9FdmVudHMvODU1NWFkMGQtMjU4YS00ZWM0LWExNDMtZWY1ODlmYTFiYjhmL2VlMThjNzkyLWUwYTYtNDUwMC1hOWNlLTAxZWJlNWIzNjJhZi9TdHJlYW0vKiIsIkNvbmRpdGlvbiI6eyJEYXRlTGVzc1RoYW4iOnsiQVdTOkVwb2NoVGltZSI6MTc3NDg3ODkxMH19fV19&Signature=J2xRMgJAsNq6qLgmYEyZnlFR~OyULVqBBkX2pWOqJC1LUyJSr3hlk0vwCBwnSTNNApbHL2q~MrgYglH3pHFpHNrUpex-~MuFeT3czRmPUQREPQeLzK-~pZyBAZVqPp0DFs2rZspGYBKWYrGUUJLiv494BUM2g8wqG6IDazZVA5~LVVqwvIoaJLVzEhUq6d3gVpRCm4irpv0L1-4XVoLmfEPSv45Cn4o1pQPykNJz-zmz6NPNGN5a~QkmtBfS7ZqHPjCzII8qgb8i20cPSPa8XIQ~7b~KJCpU~BW8IrlqJoUC11-v1HE6P5CpaBv4Vg~5R-pdwpK4sHD4rtiAK6tA6w__&Key-Pair-Id=K26LUO2Q3QP805',
        mimeType: 'application/x-mpegURL',
        duration: 5,
        startTime: 5000,
    },
    {
        url: 'http://localhost:8080/opener.mp4',
        mimeType: 'video/mp4',
        duration: 5,
        startTime: 0,
    }
]

export const CONTENT_PRESET_LABEL = 'Preset'

export const SSE_JOB_CHANNEL = 'jobs'
export const SSE_OUTPUT_CHANNEL = 'output'

export enum SSE_ENTITY{
    LOGIC_CLIP = 'logic_clip',
    KEY_MOMENT = 'key_moment',
    MATERIALIZATION = 'av',
    KEY_MOMENT_CUT = 'cut',
    COLLECTION = 'collection'
}

export enum SSE_OPERATION{
    CREATE = 'create',
    UPDATE = 'update',
    DELETE = 'delete',
}

export const SELECTION_LIMIT_MINUTES = (import.meta as any).env.VITE_KEY_MOMENT_SELECTION_LIMIT_MINUTES ? parseInt((import.meta as any).env.VITE_KEY_MOMENT_SELECTION_LIMIT_MINUTES) : 30

export const lockedKeyMomentTypes = {
    [SUPPORTED_SPORTS.FOOTBALL]:{
        [SUPPORTED_CONTENTS.FOOTBALL.MATCH_HIGHLIGHT]: ["GOAL", "START", "END"],
        [SUPPORTED_CONTENTS.FOOTBALL.GOAL_PARADE]: ["GOAL"],
        [SUPPORTED_CONTENTS.FOOTBALL.CLIP]: []
    }
}

export const MIN_MAX_MINIMUM_SECONDS_DISTANCE = {
    [SUPPORTED_SPORTS.FOOTBALL]:{
        [SUPPORTED_CONTENTS.FOOTBALL.MATCH_HIGHLIGHT]: 30,
        [SUPPORTED_CONTENTS.FOOTBALL.GOAL_PARADE]: 30,
        [SUPPORTED_CONTENTS.FOOTBALL.CLIP]: 0
    }
}

